import { Router } from 'vue-router'
import { apiBaseUrl, getProductImage } from '@/global/state/GlobalState'

export function formatBRL(value: number | null | undefined): string {
    return Number(value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export function hasPromotion(product: any): boolean {
    return !!product?.promotionalPrice && product.promotionalPrice < product.price
}

/** Same convention as banners `source`: absolute URL, `/storage/...` path or a bare storage path. */
export function resolveMediaUrl(url?: string | null): string {
    if (!url) return ''
    if (/^(https?:)?\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:')) return url
    if (url.startsWith('/')) return `${apiBaseUrl}${url}`
    return `${apiBaseUrl}/storage/${url}`
}

export function isExternalLink(link?: string | null): boolean {
    if (!link) return false
    return /^(https?:)?\/\//i.test(link) || /^(mailto|tel|whatsapp):/i.test(link)
}

/** Internal paths go through the router; external URLs open in a new tab. */
export function openStorefrontLink(router: Router, link?: string | null) {
    if (!link) return
    const trimmed = link.trim()
    if (!trimmed) return
    if (isExternalLink(trimmed)) {
        window.open(trimmed, '_blank', 'noopener')
        return
    }
    if (/^www\./i.test(trimmed)) {
        window.open(`https://${trimmed}`, '_blank', 'noopener')
        return
    }
    router.push(trimmed.startsWith('/') ? trimmed : `/${trimmed}`)
}

/** Image of a category when it really has one (no placeholder). */
export function getCategoryImage(category: any): string | null {
    const image = getProductImage(category)
    return image && !image.endsWith('/assets/no-image.png') ? image : null
}

export interface StoreStatus {
    open: boolean
    label: string
}

function minutesOf(time?: string | null): number | null {
    if (!time) return null
    const [h, m] = time.split(':').map((v) => parseInt(v, 10))
    if (Number.isNaN(h)) return null
    return h * 60 + (Number.isNaN(m) ? 0 : m)
}

/**
 * Open/closed right now from `restaurant.schedules` (same source as InfoSection:
 * `{ day: 0-6, openingTime: 'HH:MM:SS', closingTime: 'HH:MM:SS' }`). Handles schedules that
 * close after midnight. Returns null when the store has no schedules loaded.
 */
export function getStoreStatus(restaurant: any, now = new Date()): StoreStatus | null {
    const schedules: any[] | undefined = restaurant?.schedules
    if (!Array.isArray(schedules) || !schedules.length) return null
    const day = now.getDay()
    const current = now.getHours() * 60 + now.getMinutes()

    const yesterday = schedules.find((s) => s.day == (day + 6) % 7)
    const yOpen = minutesOf(yesterday?.openingTime)
    const yClose = minutesOf(yesterday?.closingTime)
    if (yOpen != null && yClose != null && yClose < yOpen && current < yClose) {
        return { open: true, label: `Aberto até ${yesterday.closingTime.slice(0, 5)}` }
    }

    const today = schedules.find((s) => s.day == day)
    const open = minutesOf(today?.openingTime)
    const close = minutesOf(today?.closingTime)
    if (open == null || close == null) return { open: false, label: 'Fechado hoje' }
    const overnight = close <= open
    const isOpen = overnight ? current >= open : current >= open && current < close
    if (isOpen) return { open: true, label: `Aberto até ${today.closingTime.slice(0, 5)}` }
    if (current < open) return { open: false, label: `Fechado · abre às ${today.openingTime.slice(0, 5)}` }
    return { open: false, label: 'Fechado agora' }
}
